import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Phone, Mail, MapPin, CheckCircle2, AlertCircle, Loader2, Send, MessageCircle } from 'lucide-react';
import api from '../api';
import { openSupportChat } from '../api/support';
import { supportText } from '../locales/support';

// Declare global grecaptcha added by the injected script
declare global {
  interface Window {
    grecaptcha: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? '';

const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export default function Contacts() {
  const { t, language } = useLanguage();
  const support = supportText[language];

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string; message?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const scriptLoadedRef = useRef(false);
  const submission = useRef<{ payload: string; id: string } | null>(null);

  // Load reCAPTCHA v3 script once and hide the floating badge via CSS
  useEffect(() => {
    if (!SITE_KEY || scriptLoadedRef.current || window.__GL_PRERENDER__) return;
    scriptLoadedRef.current = true;

    // Inject reCAPTCHA script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    script.async = true;
    document.head.appendChild(script);

    // Hide the floating badge — we display our own inline disclaimer per Google ToS
    // (also handled globally in index.css, this removes it on unmount cleanup)
    return () => {
      // no-op: badge is hidden via global CSS
    };
  }, []);

  const validate = (): boolean => {
    const e: typeof errors = {};
    if (!name.trim() || name.trim().length < 2 || name.trim().length > 100) e.name = t('contacts.formErrName');
    if (!isValidEmail(email)) e.email = t('contacts.formErrEmail');
    if (!message.trim() || message.trim().length < 10 || message.trim().length > 5000) e.message = t('contacts.formErrMessage');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');
    if (submitting || !validate()) return;
    setSubmitting(true);
    try {
      // Require SITE_KEY — bail early with a clear dev-time error if not configured
      if (!SITE_KEY) {
        setErrorMsg(t('contacts.formErrCaptcha'));
        return;
      }

      // Get fresh token immediately before sending (tokens are one-time, ~2 min TTL)
      const recaptcha_token = await new Promise<string>((resolve, reject) => {
        const unavailable = () => reject(Object.assign(new Error('Captcha unavailable'), { code: 'CAPTCHA_UNAVAILABLE' }));
        const timeout = window.setTimeout(unavailable, 12000);
        if (!window.grecaptcha?.ready) { window.clearTimeout(timeout); unavailable(); return; }
        window.grecaptcha.ready(() => {
          // Google returns a Promise-like object in some releases, without
          // native .finally(). Normalize it before handling completion.
          try {
            Promise.resolve(window.grecaptcha.execute(SITE_KEY, { action: 'contact' })).then(
              token => { window.clearTimeout(timeout); resolve(token); },
              () => { window.clearTimeout(timeout); unavailable(); },
            );
          } catch { window.clearTimeout(timeout); unavailable(); }
        });
      });
      if (!recaptcha_token) {
        setErrorMsg(t('contacts.formErrCaptcha'));
        return;
      }

      const payload = JSON.stringify([name.trim(), email.trim(), message.trim()]);
      if (submission.current?.payload !== payload) {
        // Keep the same id after a lost response; editing the form starts a
        // different submission. Browser-generated UUID has no personal data.
        const bytes = crypto.getRandomValues(new Uint8Array(16));
        bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
        const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
        submission.current = { payload, id: `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}` };
      }
      await api.post('/contact/', {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        recaptcha_token,
        submission_id: submission.current.id,
      }, { timeout: 15000 });

      setSuccessMsg(t('contacts.formSuccess'));
      setName('');
      setEmail('');
      setMessage('');
      setErrors({});
      submission.current = null;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 400) {
        setErrorMsg(t('contacts.formErrBot'));
      } else if (status === 503 || err?.code === 'CAPTCHA_UNAVAILABLE') {
        setErrorMsg(t('contacts.formErrCaptcha'));
      } else {
        setErrorMsg(t('contacts.formErrNetwork'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <div className="mb-10">
        <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4">{t('contacts.title')}</h1>
        <p className="text-lg text-gray-600">{t('contacts.subtitle')}</p>
      </div>

      <section className="mb-8 flex flex-col gap-5 rounded-2xl border border-goszakup-200 bg-goszakup-50 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div><h2 className="font-bold text-slate-900">{support.intro}</h2><p className="mt-1 text-sm leading-relaxed text-slate-600">{support.description}</p></div>
        <button type="button" onClick={openSupportChat} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-goszakup-600 px-5 py-3 text-sm font-semibold text-white hover:bg-goszakup-700">
          <MessageCircle className="h-4 w-4" />{support.open}
        </button>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center hover:shadow-md transition-shadow">
          <div className="w-14 h-14 bg-goszakup-50 text-goszakup-600 rounded-full flex items-center justify-center mb-6">
            <MapPin className="w-7 h-7" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t('contacts.address')}</h3>
          <p className="text-gray-600 font-medium">{t('contacts.addressText')}</p>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center hover:shadow-md transition-shadow">
          <div className="w-14 h-14 bg-goszakup-50 text-goszakup-600 rounded-full flex items-center justify-center mb-6">
            <Phone className="w-7 h-7" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t('contacts.phone')}</h3>
          <p className="text-gray-600 font-medium">{t('contacts.phoneText')}</p>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center hover:shadow-md transition-shadow">
          <div className="w-14 h-14 bg-goszakup-50 text-goszakup-600 rounded-full flex items-center justify-center mb-6">
            <Mail className="w-7 h-7" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t('contacts.email')}</h3>
          <p className="text-gray-600 font-medium">{t('contacts.emailText')}</p>
        </div>
      </div>

      {/* Contact Form */}
      <div className="mt-10 bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('contacts.formTitle')}</h3>

        {/* Success state */}
        {successMsg && (
          <div className="flex items-start gap-3 bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 mb-6">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-green-600" />
            <p className="text-sm font-medium">{successMsg}</p>
          </div>
        )}

        {/* Error state */}
        {errorMsg && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" />
            <p className="text-sm font-medium">{errorMsg}</p>
          </div>
        )}

        <form className="space-y-5" onSubmit={handleSubmit} noValidate>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Name */}
            <div>
              <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-1">{t('contacts.formName')}</label>
              <input
                id="contact-name" autoComplete="name" maxLength={100}
                type="text"
                value={name}
                onChange={e => { setName(e.target.value); if (errors.name) setErrors(prev => ({ ...prev, name: undefined })); }}
                placeholder={t('contacts.formNamePlaceholder')}
                className={`w-full px-4 py-3 rounded-xl border text-sm transition-all outline-none focus:ring-2 focus:ring-goszakup-500 focus:border-goszakup-500 ${errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
              />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1">{t('contacts.formEmail')}</label>
              <input
                id="contact-email" autoComplete="email" maxLength={254}
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); if (errors.email) setErrors(prev => ({ ...prev, email: undefined })); }}
                placeholder={t('contacts.formEmailPlaceholder')}
                className={`w-full px-4 py-3 rounded-xl border text-sm transition-all outline-none focus:ring-2 focus:ring-goszakup-500 focus:border-goszakup-500 ${errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
            </div>
          </div>

          {/* Message */}
          <div>
            <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-1">{t('contacts.formMessage')}</label>
            <textarea
              id="contact-message" maxLength={5000}
              rows={5}
              value={message}
              onChange={e => { setMessage(e.target.value); if (errors.message) setErrors(prev => ({ ...prev, message: undefined })); }}
              placeholder={t('contacts.formMessagePlaceholder')}
              className={`w-full px-4 py-3 rounded-xl border text-sm transition-all outline-none focus:ring-2 focus:ring-goszakup-500 focus:border-goszakup-500 resize-none ${errors.message ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
            />
            <div className="flex justify-between mt-1">
              {errors.message
                ? <p className="text-xs text-red-500">{errors.message}</p>
                : <span />
              }
              <span className={`text-xs ${message.length > 5000 ? 'text-red-500' : 'text-gray-400'}`}>{message.length}/5000</span>
            </div>
          </div>

          {/* Submit */}
          <div className="flex flex-col items-start gap-3 pt-1">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-7 py-3 bg-goszakup-600 text-white font-bold rounded-xl hover:bg-goszakup-700 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting
                ? <><Loader2 className="w-4 h-4 animate-spin" />{t('contacts.formSubmitting')}</>
                : <><Send className="w-4 h-4" />{t('contacts.formSubmit')}</>
              }
            </button>

            {/* reCAPTCHA v3 inline disclaimer (replaces floating badge, per Google ToS) */}
            {SITE_KEY && (
              <p className="text-xs text-gray-400 leading-relaxed">
                {t('contacts.captchaDisclaimer')}{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">{t('contacts.captchaPrivacy')}</a>
                {' '}{t('contacts.captchaAnd')}{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">{t('contacts.captchaTerms')}</a>
                {' '}{t('contacts.captchaGoogle')}
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
